import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box } from '@mui/material';

// Import components
import Layout from './components/common/Layout';
import Dashboard from './components/dashboard/Dashboard';
import Companies from './components/companies/Companies';
import Users from './components/users/Users';
import Credits from './components/credits/Credits';
import Languages from './components/languages/Languages';
import CompanyHub from './components/companyHub/CompanyHub';
import InterviewSessions from './components/interviews/InterviewSessions';
import TokenUsage from './components/tokenUsage/TokenUsage';
import Login from './components/common/Login';
import SecureRecordingPlayer from './components/interviews/report/SecureRecordingPlayer';

// Import services
import authService from './services/auth';

// Lightweight MUI theme bridging to the single-source index.css theme system
const theme = createTheme({
  palette: {
    primary: {
      main: '#1E3A8A',
      light: '#3B82F6',
      dark: '#1D4ED8',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#0EA5E9',
      light: '#38BDF8',
      dark: '#0284C7',
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#DC2626',
      light: '#EF4444',
      dark: '#B91C1C',
    },
    warning: {
      main: '#D97706',
      light: '#F59E0B',
      dark: '#B45309',
    },
    info: {
      main: '#0284C7',
      light: '#0EA5E9',
      dark: '#0369A1',
    },
    success: {
      main: '#059669',
      light: '#10B981',
      dark: '#047857',
    },
    background: {
      default: '#F8FAFC',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#0F172A',
      secondary: '#475569',
    },
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: { fontFamily: '"Outfit", "Inter", sans-serif', fontWeight: 800 },
    h2: { fontFamily: '"Outfit", "Inter", sans-serif', fontWeight: 700 },
    h3: { fontFamily: '"Outfit", "Inter", sans-serif', fontWeight: 700 },
    h4: { fontFamily: '"Outfit", "Inter", sans-serif', fontWeight: 600 },
    h5: { fontFamily: '"Outfit", "Inter", sans-serif', fontWeight: 600 },
    h6: { fontFamily: '"Outfit", "Inter", sans-serif', fontWeight: 600 },
    button: { fontFamily: '"Inter", sans-serif', fontWeight: 600, textTransform: 'none' },
  },
  shape: {
    borderRadius: 10,
  },
});

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const checkAuth = () => {
    const authenticated = authService.isAuthenticated();
    const superAdmin = authService.isSuperAdmin();

    console.log('checkAuth called:', { authenticated, superAdmin });

    setIsAuthenticated(authenticated);
    setIsSuperAdmin(superAdmin);
    setIsLoading(false);
  };

  useEffect(() => {
    // Check authentication status on app load
    checkAuth();
  }, []);

  const handleLogout = () => {
    console.log('Logout called');
    authService.logout();
    setIsAuthenticated(false);
    setIsSuperAdmin(false);
  };

  const handleLoginSuccess = () => {
    console.log('Login success callback called');
    // Refresh authentication state after successful login
    checkAuth();
  };

  if (isLoading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'background.default'
          }}
        >
          <div>Loading...</div>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
        <Router>
          <Routes>
            <Route
              path="/login"
              element={
                isAuthenticated && isSuperAdmin ? (
                  <Navigate to="/dashboard" replace />
                ) : (
                  <Login onLoginSuccess={handleLoginSuccess} />
                )
              }
            />
            {/* Same recording player company admins get — outside Layout chrome. */}
            <Route
              path="/secure-recording/:sessionId"
              element={
                isAuthenticated && isSuperAdmin ? (
                  <SecureRecordingPlayer />
                ) : (
                  <Navigate to="/login" replace state={{ redirectTo: window.location.pathname + window.location.search }} />
                )
              }
            />
            <Route
              path="/*"
              element={
                isAuthenticated && isSuperAdmin ? (
                  <Layout onLogout={handleLogout}>
                    <Routes>
                      <Route path="/" element={<Navigate to="/dashboard" replace />} />
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/companies" element={<Companies />} />
                      <Route path="/users" element={<Users />} />
                      <Route path="/credits" element={<Credits />} />
                      <Route path="/languages" element={<Languages />} />
                      <Route path="/company-hub" element={<CompanyHub />} />
                      <Route path="/interviews" element={<InterviewSessions />} />
                      <Route path="/token-usage" element={<TokenUsage />} />
                    </Routes>
                  </Layout>
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
          </Routes>
        </Router>
      </Box>
    </ThemeProvider>
  );
}

export default App;
